import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { type ContactProblem, contactProblem, contactShownText } from "./portalContact";
import { FormSection } from "./FormSection";
import { SkeletonSection } from "./Skeleton";

const TITLE = "Customer portal contact";
const TEXT = "The phone and the email that the customers see in the customer portal.";

/** 080 M5: the phone and the email that the customers see in the customer portal. */
export function PortalContactSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.PortalContactSetting | "failed" | "none" | undefined>(undefined);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState<ContactProblem | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const phoneInput = useRef<HTMLInputElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.PortalContactSetting) => {
    setFound(next);
    setPhone(next.phone);
    setEmail(next.email);
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.portalContact)
      .then(show, (e: unknown) => {
        // An older API has no route: render no section, so the page has no empty one.
        console.warn("portal contact: load failed", e);
        setFound(e instanceof api.ApiError && e.status === 404 ? "none" : "failed");
      });
  }, [user]);

  if (found === undefined) {
    return (
      <FormSection id="portal-contact" title={TITLE} description={TEXT}>
        <SkeletonSection title={TITLE} rows={5} />
      </FormSection>
    );
  }
  if (found === "none") return null;
  if (found === "failed") {
    return (
      <FormSection id="portal-contact" title={TITLE} description={TEXT}>
        <fieldset>
          <legend>Customer portal contact</legend>
          <p className="error">Cannot load the customer portal contact. Try again later.</p>
        </fieldset>
      </FormSection>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const wrong = contactProblem(phone, email);
    setProblem(wrong);
    setMessage(null);
    if (wrong) {
      (wrong.field === "phone" ? phoneInput : emailInput).current?.focus();
      return;
    }
    setSaving(true);
    const started = performance.now();
    try {
      const next = await api.setPortalContact(await user.getIdToken(), { phone: phone.trim(), email: email.trim() });
      console.info(`portal contact: saved in ${Math.round(performance.now() - started)} ms`);
      show(next);
      setMessage({ ok: true, text: `Saved. ${contactShownText(next.phone, next.email)}` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      console.warn("portal contact: save failed", e);
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The contact was not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  const field = (name: "phone" | "email") => ({
    "aria-invalid": problem?.field === name || undefined,
    "aria-describedby": problem?.field === name ? "portal-contact-error portal-contact-hint" : "portal-contact-hint",
  });
  return (
    <FormSection id="portal-contact" title={TITLE} description={TEXT}>
      <form onSubmit={save} noValidate data-testid="portal-contact-setting">
        <fieldset disabled={!found.can_edit || saving}>
          <legend>Customer portal contact</legend>
          <div className="limit">
            <label htmlFor="portal-contact-phone">Phone for customers</label>
            <input
              id="portal-contact-phone"
              data-testid="portal-contact-phone"
              ref={phoneInput}
              type="tel"
              autoComplete="tel"
              maxLength={found.phone_max}
              value={phone}
              {...field("phone")}
              onChange={(e) => {
                setPhone(e.target.value);
                setProblem(null);
                setMessage(null);
              }}
            />
          </div>
          <div className="limit">
            <label htmlFor="portal-contact-email">Email for customers</label>
            <input
              id="portal-contact-email"
              data-testid="portal-contact-email"
              ref={emailInput}
              type="email"
              autoComplete="email"
              maxLength={found.email_max}
              value={email}
              {...field("email")}
              onChange={(e) => {
                setEmail(e.target.value);
                setProblem(null);
                setMessage(null);
              }}
            />
          </div>
          {problem && (
            <p id="portal-contact-error" role="alert" className="error" data-testid="portal-contact-error">
              {problem.text}
            </p>
          )}
          <p id="portal-contact-hint" className="hint">
            {contactShownText(found.phone, found.email)} Leave a field empty to not show it.
            {found.can_edit ? "" : " Only an administrator can change it."}
          </p>
          {found.can_edit && (
            <div className="actions">
              <button className="primary" data-testid="save-portal-contact">
                {saving ? "Saving…" : "Save the contact"}
              </button>
            </div>
          )}
        </fieldset>
        <p ref={status} tabIndex={-1} role="status" data-testid="portal-contact-status" className={message && !message.ok ? "error" : undefined}>
          {message?.text}
        </p>
      </form>
    </FormSection>
  );
}
